import type {
  Commonv1Value,
  V1AutomationConfig,
  V1CallArg,
  V1ValueType,
} from '@/generated/hooks/grimoire/models';
import type { TResolvedValue } from '@/modules/outputs/types';
import { isBrowserInstance, resolveCommonValue } from '@/modules/outputs/utils';

/** One argument of an external call, ready for `ValueList`. */
export interface IResolvedCallArg {
  name: string;
  /** `undefined` until known: a computed argument in the plan, or a secret. */
  value?: TResolvedValue;
  secret: boolean;
  fromTweak: boolean;
}

function isSecretType(type?: V1ValueType): boolean {
  if (!type) return false;
  if (type.sensitive_type) return true;
  return isSecretType(type.optional_type?.inner);
}

/** `isBrowserInstance`'s rule, read off a planned argument's type. */
function isBrowserSessionType(type?: V1ValueType): boolean {
  const inner = type?.optional_type?.inner ?? type;
  const keys = new Set(
    (inner?.dictionary_type?.fields ?? []).map(field => field.key),
  );
  return (
    (keys.has('browser_name') || keys.has('session_id')) &&
    (keys.has('live_view_url') || keys.has('display_url'))
  );
}

/**
 * Resolves an external call's arguments. With `runInputs` (the values a run
 * actually passed) those win; otherwise literals and config-backed arguments
 * resolve from the plan and computed ones stay unknown. Secrets are never
 * resolved, and browser session handles are left out. Known values sort first.
 *
 * @param args - The call's keyword arguments, in source order
 * @param automationConfig - Resolves `config`-backed arguments to their value
 * @param runInputs - The inputs a run passed to this call, when one is open
 * @returns One entry per argument, known values first
 */
export function resolveCallArgs(
  args: V1CallArg[] | undefined,
  automationConfig: V1AutomationConfig | undefined,
  runInputs?: Record<string, Commonv1Value>,
): IResolvedCallArg[] {
  const isSession = (value: TResolvedValue | undefined) =>
    value !== undefined && isBrowserInstance(value);
  const resolved = (args ?? [])
    .filter(arg => !isBrowserSessionType(arg.type))
    .map((arg): IResolvedCallArg => {
      const name = arg.name ?? '';
      const secret = isSecretType(arg.type);
      const source =
        runInputs?.[name] ??
        arg.literal ??
        (arg.config ? automationConfig?.[arg.config]?.value : undefined);

      return {
        name,
        value:
          secret || source === undefined
            ? undefined
            : resolveCommonValue(source),
        secret,
        fromTweak: Boolean(arg.config),
      };
    });

  const planned = new Set(resolved.map(arg => arg.name));
  for (const [name, source] of Object.entries(runInputs ?? {})) {
    if (planned.has(name)) continue;
    resolved.push({
      name,
      value: resolveCommonValue(source),
      secret: false,
      fromTweak: false,
    });
  }

  const shown = resolved.filter(arg => !isSession(arg.value));
  return [
    ...shown.filter(arg => arg.value !== undefined),
    ...shown.filter(arg => arg.value === undefined),
  ];
}
